"use client";

import type { ChangeEvent } from "react";
import { useState } from "react";
import {
  BdCalculator,
  BdChart2,
  BdDollarMinimalistic,
} from "solar-icon-react/bd";
import { cn } from "@/lib";

type Mode = "interest" | "rate";

type Result = {
  totalInterest: number;
  dailyInterest: number;
  monthlyInterest: number;
  annualInterest: number;
  periodPercent: number;
  totalValue: number;
  equivalentAnnualRate?: number;
  interestPerTenMillion: number;
};

const ratePresets = [5, 20.5, 21.5, 22.5];
const dayPresets = [30, 90, 180, 365, 730, 1095];

const modeItems: { key: Mode; title: string; description: string }[] = [
  {
    key: "interest",
    title: "محاسبه سود سپرده",
    description:
      "مبلغ سپرده، نرخ سالانه و مدت نگهداری پول را وارد کنید تا سود دوره محاسبه شود.",
  },
  {
    key: "rate",
    title: "یافتن نرخ موثر",
    description:
      "اگر مبلغ سود دریافتی را می‌دانید، نرخ سالانه معادل آن را محاسبه کنید.",
  },
];

const toEnglishDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)));

const parseNumber = (value: string) =>
  Number(
    toEnglishDigits(value)
      .replace(/,/g, "")
      .replace(/[^\d.]/g, ""),
  ) || 0;

const formatMoney = (value: number) =>
  `${Math.round(value).toLocaleString("fa-IR")} تومان`;

const formatPercent = (value: number) =>
  Number.isFinite(value) ? `${value.toFixed(2).replace(/\.00$/, "")}٪` : "-";

const formatInputMoney = (value: string) => {
  const parsed = parseNumber(value);
  return parsed ? parsed.toLocaleString("en-US") : "";
};

const calculateInterest = ({
  amount,
  rate,
  days,
}: {
  amount: number;
  rate: number;
  days: number;
}): Result | null => {
  if (!amount || !rate || !days) return null;

  const totalInterest = (amount * rate * days) / 36500;
  const dailyInterest = totalInterest / days;
  const monthlyInterest = (amount * rate * 30) / 36500;
  const annualInterest = (amount * rate * 365) / 36500;
  const periodPercent = (totalInterest / amount) * 100;
  const interestPerTenMillion = (10_000_000 * rate * days) / 36500;

  return {
    totalInterest,
    dailyInterest,
    monthlyInterest,
    annualInterest,
    periodPercent,
    totalValue: amount + totalInterest,
    interestPerTenMillion,
  };
};

const calculateRate = ({
  amount,
  interest,
  days,
}: {
  amount: number;
  interest: number;
  days: number;
}): Result | null => {
  if (!amount || !interest || !days) return null;

  const equivalentAnnualRate = (interest * 36500) / (amount * days);
  const monthlyInterest = (amount * equivalentAnnualRate * 30) / 36500;
  const annualInterest = (amount * equivalentAnnualRate * 365) / 36500;
  const periodPercent = (interest / amount) * 100;
  const interestPerTenMillion =
    (10_000_000 * equivalentAnnualRate * days) / 36500;

  return {
    totalInterest: interest,
    dailyInterest: interest / days,
    monthlyInterest,
    annualInterest,
    periodPercent,
    totalValue: amount + interest,
    equivalentAnnualRate,
    interestPerTenMillion,
  };
};

const DepositInterestCalculator = () => {
  const [mode, setMode] = useState<Mode>("interest");
  const [amount, setAmount] = useState("");
  const [rate, setRate] = useState("");
  const [days, setDays] = useState("");
  const [receivedInterest, setReceivedInterest] = useState("");
  const [result, setResult] = useState<Result | null>(null);
  const [hasCalculated, setHasCalculated] = useState(false);

  const selectedMode = modeItems.find((item) => item.key === mode);

  const calculate = (
    overrides: Partial<
      Record<"amount" | "rate" | "days" | "receivedInterest", string>
    > = {},
  ) => {
    const nextAmount = parseNumber(overrides.amount ?? amount);
    const nextRate = parseNumber(overrides.rate ?? rate);
    const nextDays = Math.floor(parseNumber(overrides.days ?? days));
    const nextReceivedInterest = parseNumber(
      overrides.receivedInterest ?? receivedInterest,
    );

    const nextResult =
      mode === "interest"
        ? calculateInterest({
            amount: nextAmount,
            rate: nextRate,
            days: nextDays,
          })
        : calculateRate({
            amount: nextAmount,
            interest: nextReceivedInterest,
            days: nextDays,
          });

    setHasCalculated(true);
    setResult(nextResult);
  };

  const resetResult = () => {
    if (!hasCalculated) return;
    setResult(null);
    setHasCalculated(false);
  };

  const handleMoneyChange =
    (setter: (value: string) => void) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setter(formatInputMoney(event.target.value));
      resetResult();
    };

  return (
    <div className="relative w-full rounded-2xl bg-linear-to-b from-yellow-500 to-transparent p-[2px] shadow-[0_24px_70px_rgba(15,23,42,0.08)] sm:rounded-3xl">
      <div className="grid size-full items-stretch gap-5 rounded-[18px] bg-white p-3 sm:gap-6 sm:rounded-[22px] sm:p-4 lg:grid-cols-2 lg:p-6">
        <section className="order-1 flex h-full flex-col">
          <div className="border-primary-main/15 bg-primary-main/[0.03] mb-4 flex items-center gap-3 rounded-2xl border p-3 sm:p-4">
            <span className="bg-primary-main/10 text-primary-main flex size-10 shrink-0 items-center justify-center rounded-2xl sm:size-12">
              <BdDollarMinimalistic size={30} />
            </span>
            <div className="min-w-0">
              <h2 className="text-base leading-8 font-bold text-slate-900 sm:text-xl">
                محاسبه سود سپرده بانکی
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                رایگان · آنلاین · بدون ثبت‌نام
              </p>
            </div>
          </div>

          <div className="border-primary-main/15 mb-4 rounded-2xl border p-4">
            <h3 className="font-bold text-slate-800">{selectedMode?.title}</h3>
            <p className="mt-2 text-sm leading-7 text-slate-500">
              {selectedMode?.description}
            </p>
          </div>

          <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {modeItems.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => {
                  setMode(item.key);
                  resetResult();
                }}
                className={cn(
                  "border-primary-main/15 hover:border-primary-main/35 min-h-14 rounded-xl border bg-white px-4 py-3 text-sm font-bold text-slate-700 transition",
                  mode === item.key &&
                    "bg-primary-main text-white shadow-[0_10px_24px_rgba(65,56,168,0.25)]",
                )}
              >
                {item.title}
              </button>
            ))}
          </div>

          <div className="grid gap-4 min-[520px]:grid-cols-2">
            <InputBlock
              label="مبلغ سپرده (تومان)"
              value={amount}
              onChange={handleMoneyChange(setAmount)}
              placeholder="مثلا 100,000,000"
            />
            {mode === "interest" ? (
              <InputBlock
                label="نرخ سود سالانه (%)"
                value={rate}
                onChange={(event) => {
                  setRate(toEnglishDigits(event.target.value));
                  resetResult();
                }}
                placeholder="مثلا 22.5"
              />
            ) : (
              <InputBlock
                label="سود دریافتی (تومان)"
                value={receivedInterest}
                onChange={handleMoneyChange(setReceivedInterest)}
                placeholder="مثلا 5,500,000"
              />
            )}
            <InputBlock
              label="مدت سپرده‌گذاری (روز)"
              value={days}
              onChange={(event) => {
                setDays(toEnglishDigits(event.target.value));
                resetResult();
              }}
              placeholder="مثلا 365"
            />
            <div className="border-primary-main/10 bg-primary-main/[0.025] flex min-h-14 items-center rounded-xl border px-4 text-sm leading-7 text-slate-600">
              فرمول سود ساده: مبلغ × نرخ سالانه × روز ÷ 36500
            </div>
          </div>

          <div className="mt-4 flex flex-wrap justify-center gap-2 sm:justify-start">
            {mode === "interest" &&
              ratePresets.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    const nextRate = String(item);
                    setRate(nextRate);
                    calculate({ rate: nextRate });
                  }}
                  className="border-primary-main/15 text-primary-main hover:bg-primary-main/[0.04] rounded-full border px-3 py-1 text-xs font-bold transition"
                >
                  {item}٪
                </button>
              ))}
            {dayPresets.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  const nextDays = String(item);
                  setDays(nextDays);
                  calculate({ days: nextDays });
                }}
                className="border-primary-main/15 text-primary-main hover:bg-primary-main/[0.04] rounded-full border px-3 py-1 text-xs font-bold transition"
              >
                {item} روز
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => calculate()}
            className="bg-primary-main mt-5 h-14 rounded-2xl text-base font-bold text-white shadow-[0_12px_28px_rgba(65,56,168,0.25)] transition hover:brightness-105"
          >
            محاسبه سود سپرده
          </button>

          <div className="bg-primary-main/[0.03] mt-5 rounded-2xl p-3 text-sm leading-7 text-slate-600 sm:mt-auto sm:p-4">
            نتیجه این ابزار بر اساس سود ساده محاسبه می‌شود. در بعضی سپرده‌ها،
            شرایط بانک، مالیات، برداشت زودتر از موعد یا نحوه واریز سود می‌تواند
            عدد نهایی را کمی تغییر دهد.
          </div>
        </section>

        <section className="border-primary-main/20 bg-primary-main/[0.02] order-2 flex h-full min-w-0 flex-col rounded-2xl border border-dashed p-3 sm:p-4">
          <div className="mb-5 flex items-start gap-3">
            <span className="bg-primary-main/10 text-primary-main flex size-10 shrink-0 items-center justify-center rounded-2xl">
              <BdChart2 size={26} />
            </span>
            <div>
              <h3 className="text-lg font-bold text-slate-800">
                خروجی سود سپرده همین‌جا نمایش داده می‌شود
              </h3>
              <p className="mt-2 text-sm leading-7 text-slate-600">
                بعد از محاسبه، سود کل دوره، سود روزانه، سود ماهانه، سود سالانه و
                جمع اصل سپرده با سود را می‌بینید.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 min-[430px]:grid-cols-2">
            <SummaryCard label="سود کل دوره" value={result?.totalInterest} />
            <SummaryCard label="سود روزانه" value={result?.dailyInterest} />
            <SummaryCard label="سود ۳۰ روزه" value={result?.monthlyInterest} />
            <SummaryCard label="سود سالانه" value={result?.annualInterest} />
            <SummaryCard
              label="درصد سود دوره"
              text={result ? formatPercent(result.periodPercent) : "-"}
            />
            <SummaryCard
              label="سود هر ۱۰ میلیون"
              value={result?.interestPerTenMillion}
            />
            {mode === "rate" && (
              <SummaryCard
                label="نرخ سالانه معادل"
                text={
                  result?.equivalentAnnualRate
                    ? formatPercent(result.equivalentAnnualRate)
                    : "-"
                }
              />
            )}
            <SummaryCard label="اصل سپرده + سود" value={result?.totalValue} />
          </div>

          {hasCalculated && !result && (
            <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm leading-7 text-amber-800">
              برای محاسبه، مبلغ سپرده و مدت را وارد کنید. در حالت محاسبه سود،
              نرخ سالانه و در حالت یافتن نرخ، مبلغ سود دریافتی هم لازم است.
            </div>
          )}

          <div className="mt-5 flex-1 rounded-2xl border border-slate-100 bg-white p-4">
            {result ? (
              <div className="space-y-4 text-sm leading-8 text-slate-700">
                <p>
                  سود سپرده شما در این بازه حدود{" "}
                  <strong className="text-primary-main">
                    {formatMoney(result.totalInterest)}
                  </strong>{" "}
                  است.
                </p>
                <p>
                  اگر اصل سپرده را هم در نظر بگیریم، جمع مبلغ نهایی حدود{" "}
                  <strong>{formatMoney(result.totalValue)}</strong> خواهد بود.
                </p>
                {mode === "rate" && result.equivalentAnnualRate ? (
                  <p>
                    نرخ سالانه معادل این سود تقریبا{" "}
                    <strong>
                      {formatPercent(result.equivalentAnnualRate)}
                    </strong>{" "}
                    است.
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="flex min-h-[220px] items-center justify-center text-center text-sm leading-7 text-slate-500">
                اطلاعات سپرده را وارد کنید و دکمه محاسبه را بزنید تا نتیجه در
                این بخش نمایش داده شود.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

const InputBlock = ({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
}) => (
  <div>
    <label className="mb-2 block text-sm font-bold text-slate-700">
      {label}
    </label>
    <div className="border-primary-main/10 focus-within:border-primary-main/45 h-14 w-full rounded-xl border bg-white px-4 transition">
      <input
        value={value}
        onChange={onChange}
        inputMode="decimal"
        placeholder={placeholder}
        className="size-full border-0 bg-transparent text-left text-sm font-bold text-slate-800 outline-none"
        dir="ltr"
      />
    </div>
  </div>
);

const SummaryCard = ({
  label,
  value,
  text,
}: {
  label: string;
  value?: number;
  text?: string;
}) => (
  <div className="border-primary-main/10 rounded-xl border bg-white p-3 text-center">
    <p className="text-primary-main text-sm font-bold">{label}</p>
    <p className="mt-2 text-base font-black text-slate-900 sm:text-lg">
      {text ?? (value ? formatMoney(value) : "-")}
    </p>
  </div>
);

export default DepositInterestCalculator;
